import { useCallback, useState, useEffect } from 'react';
import { useAdvancedMarkerRef } from '@vis.gl/react-google-maps';
import { getPrefName } from '@/data/prefectures';
import { service_ResponseForNonMemberPortalOfficeListing } from '@/init/swagger';

type LogicProps = {
  listing: service_ResponseForNonMemberPortalOfficeListing;
  hoveredMarkerListingId?: number | null;
  onMarkerClick?: (listingId: number) => void;
};

export const useLogic = ({
  listing,
  hoveredMarkerListingId,
  onMarkerClick,
}: LogicProps) => {
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [isShowInfoWindow, setIsShowInfoWindow] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  // デバイスタイプの検出
  useEffect(() => {
    const checkTouchDevice = () => {
      setIsTouchDevice(
        'ontouchstart' in window ||
          navigator.maxTouchPoints > 0 ||
          (navigator as any).msMaxTouchPoints > 0,
      );
    };

    checkTouchDevice();
    window.addEventListener('resize', checkTouchDevice);

    return () => {
      window.removeEventListener('resize', checkTouchDevice);
    };
  }, []);

  // 外部からのホバー状態を監視
  useEffect(() => {
    if (hoveredMarkerListingId === listing.id) {
      setIsShowInfoWindow(true);
      setIsHovered(true); // ステーション一覧ホバー時にマーカーを拡大表示
    } else if (
      hoveredMarkerListingId !== null &&
      hoveredMarkerListingId !== listing.id
    ) {
      // 他のオフィスがホバーされた場合は非表示
      setIsShowInfoWindow(false);
      setIsHovered(false); // 他のステーションがホバーされた時は通常サイズに戻す
    }
    // hoveredMarkerListingIdがnullの場合の処理
    else if (hoveredMarkerListingId === null) {
      // nullの場合は非表示にする（クリックによるOFF操作）
      setIsShowInfoWindow(false);
      setIsHovered(false); // ホバー解除時は通常サイズに戻す
    }
  }, [hoveredMarkerListingId, listing.id]);

  const handleMarkerClick = useCallback(() => {
    if (onMarkerClick && listing.id) {
      onMarkerClick(listing.id);
    } else {
      // フォールバック: 従来の動作
      setIsShowInfoWindow((isShow) => !isShow);
    }
  }, [onMarkerClick, listing.id]);

  // ホバーイベントハンドラー（マウス操作時のみ）
  const handleMarkerMouseEnter = useCallback(() => {
    if (!isTouchDevice) {
      setIsHovered(true);
    }
  }, [isTouchDevice]);

  const handleMarkerMouseLeave = useCallback(() => {
    if (!isTouchDevice) {
      setIsHovered(false);
    }
  }, [isTouchDevice]);

  const officeName = listing.portal_office?.name;
  const officeAddress = [
    getPrefName(listing.portal_office?.pref || 0),
    listing.portal_office?.city,
    listing.portal_office?.area,
    listing.portal_office?.building || '',
  ].join('');
  const markerPosition = {
    lat: listing.latitude as number,
    lng: listing.longitude as number,
  };
  const isIbowOffice = !!listing.portal_office?.ibow_office_id;

  return {
    officeName,
    officeAddress,
    markerPosition,
    markerRef,
    marker,
    isIbowOffice,
    isShowInfoWindow,
    handleMarkerClick,
    handleMarkerMouseEnter,
    handleMarkerMouseLeave,
    isHovered,
    isTouchDevice,
  };
};
